/** Consistent asset navigation and permission-aware, lazily loaded operational preview. */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetMeterSummary } from "@/features/assets/AssetMeterSummary";
import { useAsset } from "@/features/assets/queries";
import { EntityPreview } from "@/shared/components/entity-preview";
import { ErrorState, LoadingState, StatusBadge } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";

type AssetContext = "default" | "work-order" | "history";

function AssetPreview({ id, context }: { id: string; context: AssetContext }): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const asset = useAsset(id);
  const canWork = can("workorders.workorder.view_all") || can("workorders.workorder.view_assigned");
  const canFault = can("requests.request.view_all") || can("requests.request.view_own");
  const work = useQuery({
    queryKey: ["work-orders", "preview", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/work-orders/", {
          params: { query: { asset_id: id, open_only: true, limit: 1 } },
        }),
      ),
    enabled: canWork && asset.isSuccess,
    staleTime: 30_000,
  });
  const faults = useQuery({
    queryKey: ["requests", "preview", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/requests/", {
          params: { query: { asset_id: id, open_only: true, limit: 1 } },
        }),
      ),
    enabled: canFault && asset.isSuccess,
    staleTime: 30_000,
  });
  if (asset.isPending) return <LoadingState className="justify-start p-0 text-sm" />;
  if (asset.isError) return <ErrorState error={asset.error} onRetry={() => void asset.refetch()} />;
  const a = asset.data;
  return (
    <div className="space-y-3">
      <div>
        <p className="font-semibold">{a.name}</p>
        <p className="text-xs text-muted-foreground">
          {a.code} · {a.type.name}
        </p>
        <StatusBadge value={a.status} namespace="assets.status" />
      </div>
      <div className="space-y-1 border-y py-3 text-sm">
        {a.parent ? (
          <p>
            {t("assets.parent")}: {a.parent.name}
          </p>
        ) : null}
        <p>
          {t("app.site")}: {a.site?.name ?? "—"}
        </p>
        <p>
          {t("assets.location")}: {a.location?.name ?? "—"}
        </p>
        <AssetMeterSummary meters={a.meters} />
      </div>
      <div className="space-y-1 text-sm">
        <p>
          {t("assets.openWorkOrders")}:{" "}
          {canWork
            ? work.isPending
              ? t("app.loading")
              : (work.data?.total ?? t("assets.summary.unavailable"))
            : t("assets.summary.unavailable")}
        </p>
        {work.isError ? (
          <ErrorState error={work.error} onRetry={() => void work.refetch()} />
        ) : null}
        <p>
          {t("requests.title")}:{" "}
          {canFault
            ? faults.isPending
              ? t("app.loading")
              : (faults.data?.total ?? t("assets.summary.unavailable"))
            : t("assets.summary.unavailable")}
        </p>
        {faults.isError ? (
          <ErrorState error={faults.error} onRetry={() => void faults.refetch()} />
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button asChild size="sm">
          <Link to="/assets/$assetId" params={{ assetId: id }}>
            {t("assets.summary.open")}
          </Link>
        </Button>
        {context !== "default" && canWork ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/assets/$assetId" params={{ assetId: id }} search={{ tab: "history" }}>
              {t("entityPreview.history")}
            </Link>
          </Button>
        ) : null}
        {can("requests.request.create") ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/requests" search={{ asset_id: id }}>
              {t("requests.new")}
            </Link>
          </Button>
        ) : null}
        {context === "default" && can("workorders.workorder.create") ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/work-orders" search={{ asset_id: id }}>
              {t("workOrders.new")}
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Keep the caller's label (including historical names) separate from current preview details. */
export function AssetReference({
  id,
  name,
  code,
  context = "default",
  className,
}: {
  id: string | null | undefined;
  name: string;
  code?: string | null;
  context?: AssetContext;
  className?: string;
}): ReactElement {
  const { can } = useAuth();
  const label = code ? `${code} · ${name}` : name;
  if (!id || !can("assets.asset.view")) return <span className={className}>{label}</span>;
  return (
    <EntityPreview label={name} preview={() => <AssetPreview id={id} context={context} />}>
      <Link
        to="/assets/$assetId"
        params={{ assetId: id }}
        className={className ?? "min-w-0 text-primary hover:underline [overflow-wrap:anywhere]"}
      >
        {code ? (
          <>
            <span className="font-mono text-xs">{code}</span> ·{" "}
          </>
        ) : null}
        <span>{name}</span>
      </Link>
    </EntityPreview>
  );
}
